import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// type: "income" | "expense"
const pathByType = (type) => (type === "income" ? "income-sources" : "expense-types");

export const fetchMasterThunk = createAsyncThunk(
  "incomeExpenseMasters/fetchAll",
  async ({ type }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/masters/${pathByType(type)}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return { type, list: res.data?.data || [] };
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const createMasterThunk = createAsyncThunk(
  "incomeExpenseMasters/create",
  async ({ type, payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/api/masters/${pathByType(type)}`, payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchMasterThunk({ type }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const updateMasterThunk = createAsyncThunk(
  "incomeExpenseMasters/update",
  async ({ type, id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/api/masters/${pathByType(type)}/${id}`, payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchMasterThunk({ type }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

export const deleteMasterThunk = createAsyncThunk(
  "incomeExpenseMasters/delete",
  async ({ type, id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/api/masters/${pathByType(type)}/${id}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchMasterThunk({ type }));
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

const incomeExpenseMastersSlice = createSlice({
  name: "incomeExpenseMasters",
  initialState: {
    // hold two lists to avoid reloading when switching pages
    incomeList: [],
    expenseList: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearMastersError(state) {
      state.error = null;
    },
    clearMasters(state) {
      state.incomeList = [];
      state.expenseList = [];
      state.loading = false;
      state.saving = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchMasterThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMasterThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { type, list } = action.payload || {};
        if (type === "income") state.incomeList = list || [];
        else state.expenseList = list || [];
      })
      .addCase(fetchMasterThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load";
      })

      // saving (create/update/delete)
      .addMatcher(
        (a) =>
          a.type === createMasterThunk.pending.type ||
          a.type === updateMasterThunk.pending.type ||
          a.type === deleteMasterThunk.pending.type,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createMasterThunk.fulfilled.type ||
          a.type === updateMasterThunk.fulfilled.type ||
          a.type === deleteMasterThunk.fulfilled.type,
        (state) => {
          state.saving = false;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createMasterThunk.rejected.type ||
          a.type === updateMasterThunk.rejected.type ||
          a.type === deleteMasterThunk.rejected.type,
        (state, action) => {
          state.saving = false;
          state.error = action.payload || "Operation failed";
        }
      );
  },
});

export const { clearMastersError, clearMasters } = incomeExpenseMastersSlice.actions;
export default incomeExpenseMastersSlice.reducer;
